//@ts-nocheck
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4lang_en_US from "@amcharts/amcharts4/lang/en_US";
import am4lang_ru_RU from "@amcharts/amcharts4/lang/ru_RU";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import clsx from "clsx";
import { subMonths, subYears } from "date-fns";
import { first, last, noop } from "lodash";
import { Statistics } from "models/Statistics";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyList } from "shared/src/components/EmptyList/EmptyList";
import { NullAble } from "shared/src/helpers/ts-helpers";
import { isFull } from "../../helpers/isFull.helper";
import { formatBytes } from "../../helpers/utils";
import { usePrevious } from "../../hooks/usePrevious";
import { SvgChart } from "../../images/Chart";
import { BandwidthAnalyticsRecord, StorageUsageAnalyticsRecord } from "../models/Statistics.dto";
import style from "./charts.module.scss";
import DayPickerRange from "./DayPickerRange/DayPickerRange";

type DateAxis = am4charts.DateAxis<am4charts.AxisRenderer>;
export type GetStatisticsCallBack = {
  (data: {
    statistics: Statistics;
    bandwidthAnalytics: { Records: BandwidthAnalyticsRecord[] };
    storageUsageAnalytics: { Records: StorageUsageAnalyticsRecord[] };
  }): void;
};

function am4themes_myTheme(target: am4core.ITheme) {
  if (target instanceof am4core.ColorSet) {
    target.list = [am4core.color("#41A8ED")];
  }
}

function am4themes_myThemeColumns(target: am4core.ITheme) {
  if (target instanceof am4core.ColorSet) {
    target.list = [am4core.color("#41A8ED"), am4core.color("#47C281")];
  }
}
const today = new Date();

const CHART_LANG_MAP = {
  en: am4lang_en_US,
  ru: am4lang_ru_RU,
};

enum ChartIntervals {
  ONE_MONTH = 0,
  THREE_MONTH = 1,
  SIX_MONTH = 2,
  YEAR = 3,
  MAX = 4,
}

export type getFn = {
  (args: { from?: Date; to?: Date; callback?: (...args: any[]) => void }): void;
};

type ChartStorageProps = {
  tabsParent: number;
  dataBandwidth: BandwidthAnalyticsRecord[];
  dataStorage: StorageUsageAnalyticsRecord[];
  initialFromTo: { fromInput: Date; toInput: Date };
  get: getFn;
};

export const ChartStorageUsage: React.FC<ChartStorageProps> = (props) => {
  const { dataStorage, dataBandwidth, tabsParent = 0, initialFromTo, get } = props;
  const [tabsParentStage, setTabsParentStage] = useState<NullAble<number>>();
  const [tabs, _setTabs] = useState(0);
  const [chart1, _setChart1] = useState<NullAble<am4charts.XYChart>>(null);
  const [chart2, _setChart2] = useState<NullAble<am4charts.XYChart>>(null);
  const [dateAxis1, _setDateAxis1] = useState<NullAble<DateAxis>>(null);
  const [dateAxis2, _setDateAxis2] = useState<NullAble<DateAxis>>(null);
  const [fromTo, setFromTo] = useState(initialFromTo);
  const [dateChartInterval, setDateChartInterval] = useState(ChartIntervals.ONE_MONTH);
  const dataStorageVisible = dataStorage?.filter((item) => item.UsedStorage > 0);
  const dataBandwidthVisible = dataBandwidth?.filter((item) => item.DownloadBandwidth > 0 || item.UploadBandwidth > 0);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const prevFromTo = usePrevious(fromTo);

  const maxRange = useMemo(() => {
    const data = tabs === 0 ? dataStorage : dataBandwidth;
    const firstItem = first(data);
    const lastItem = last(data);
    const dateKey = tabs === 0 ? "Timestamp" : "Date";
    const result = { from: new Date(firstItem?.[dateKey]), to: new Date(lastItem?.[dateKey]) };
    return result;
  }, [tabs, dataBandwidth, dataStorage]);

  const dateAxis2Ref = useRef(dateAxis2);
  const chart1Ref = useRef(chart1);
  const chart2Ref = useRef(chart2);
  const tabsRef = useRef(tabs);
  const dateAxis1Ref = useRef(dateAxis1);
  const ref1 = React.createRef<DayPickerRange>();
  const ref2 = React.createRef<DayPickerRange>();
  const indicatorRef = useRef();

  const showIndicator = useCallback((chartRef: React.Ref<am4charts.XYChart>) => {
    const chartInstance = chartRef.current;
    if (!chartInstance) return;
    const indicator = chartInstance.tooltipContainer.createChild(am4core.Container);
    indicatorRef.current = () => indicator.hide(0);
    indicator.background.fill = am4core.color("#fff");
    indicator.background.fillOpacity = 0.8;
    indicator.width = am4core.percent(100);
    indicator.height = am4core.percent(100);
    let indicatorLabel = indicator.createChild(am4core.Label);
    indicatorLabel.text = `${t("Loading")}...`;
    indicatorLabel.align = "center";
    indicatorLabel.valign = "middle";
    indicatorLabel.fontSize = 20;
  }, []);
  const setTabs = (data: number) => {
    // for chart1 inside eventListener to be actual
    tabsRef.current = data;
    _setTabs(data);
  };
  const setChart1 = (data: am4charts.XYChart) => {
    // for chart1 inside eventListener to be actual
    chart1Ref.current = data;
    _setChart1(data);
  };
  const setChart2 = (data: am4charts.XYChart) => {
    // for chart1 inside eventListener to be actual
    chart2Ref.current = data;
    _setChart2(data);
  };
  const setDateAxis1 = (data: DateAxis) => {
    // for dateAxis1 inside eventListener to be actual
    dateAxis1Ref.current = data;
    _setDateAxis1(data);
  };
  const setDateAxis2 = (data: DateAxis) => {
    // for dateAxis1 inside eventListener to be actual
    dateAxis2Ref.current = data;
    _setDateAxis2(data);
  };
  const customizeGrip = (grip: am4core.ResizeButton) => {
    grip.background.fill = am4core.color("#41A8ED");
    grip.background.disabled = true;
    const img = grip.createChild(am4core.Circle);
    img.width = 23;
    img.height = 23;
    img.fill = am4core.color("#41A8ED");
    img.align = "center";
    img.valign = "middle";

    const line = grip.createChild(am4core.Rectangle);
    line.height = 60;
    line.width = 2;
    line.fill = am4core.color("#41A8ED");
    line.align = "center";
    line.valign = "middle";
  };

  const listenerButtons = (n: number, unit: "month" | "year" | "max", interval: ChartIntervals) => {
    let from: NullAble<Date>;
    let to: NullAble<Date>;
    const activeChart = tabs === 0 ? chart1Ref : chart2Ref;
    showIndicator(activeChart);

    if (unit === "month") {
      from = subMonths(today, n);
      to = today;
    }
    if (unit === "year") {
      from = subYears(today, n);
      to = today;
    }
    if (unit === "max") {
      from = undefined;
      to = undefined;
    }

    setFromTo({ fromInput: from, toInput: to });
    setDateChartInterval(interval);
  };
  const handleFromChange = (from: Date) => setFromTo((prev) => ({ ...prev, fromInput: from }));
  const handleToChange = (to: Date) => setFromTo((prev) => ({ ...prev, toInput: to }));
  const handleAddLabel = (label?: string) => {
    if (label) {
      let num = label;
      num = num.replaceAll(" ", ",");
      num = num.replace(/,/g, "");
      num = +num;

      return formatBytes(num);
    }
    return label;
  };

  useEffect(() => {
    const from = fromTo?.fromInput;
    const to = fromTo?.toInput;
    const MIN_YEAR = 2019;

    if (initialFromTo.fromInput === from || initialFromTo.toInput === to) {
      return;
    }

    try {
      if (from === undefined && to === undefined) {
        return get({ from, to });
      }
      if (from.getFullYear() > MIN_YEAR && from <= to) {
        get({ from, to });
      }
    } catch (err) {
      console.error(err);
    }
  }, [fromTo]);

  useEffect(() => {
    am4core.options.defaultLocale = CHART_LANG_MAP[language];
    if (tabs === 1 && isFull(dataBandwidth)) {
      am4core.useTheme(am4themes_animated);
      am4core.useTheme(am4themes_myThemeColumns);

      // Create chart instance
      const chart2 = am4core.create("chartdiv", am4charts.XYChart);
      chart2.zoomOutButton.disabled = true;
      chart2.paddingTop = 0;
      chart2.paddingBottom = "24px";
      chart2.paddingLeft = "24px";
      chart2.paddingRight = "24px";
      chart2.data = dataBandwidth;
      chart2.cursor = new am4charts.XYCursor();
      const dateAxis2 = chart2.xAxes.push(new am4charts.DateAxis());
      dateAxis2.baseInterval = {
        timeUnit: "day",
        count: 1,
      };
      dateAxis2.tooltipDateFormat = "d MMMM";
      dateAxis2.start = 0;
      dateAxis2.keepSelection = true;
      dateAxis2.groupData = false;
      dateAxis2.renderer.labels.template.fill = am4core.color("#1B2A47");
      dateAxis2.renderer.labels.template.location = 0.5;
      dateAxis2.cursorTooltipEnabled = false;
      setDateAxis2(dateAxis2);
      const valueAxis2 = chart2.yAxes.push(new am4charts.ValueAxis());
      valueAxis2.renderer.inside = true;
      valueAxis2.calculateTotals = true;
      valueAxis2.renderer.labels.template.fill = am4core.color("#6E7E9D");
      valueAxis2.cursorTooltipEnabled = false;
      valueAxis2.renderer.grid.template.location = 0.5;
      valueAxis2.renderer.labels.template.dy = -10;
      valueAxis2.renderer.maxLabelPosition = 0.9;
      valueAxis2.min = 0;
      valueAxis2.renderer.labels.template.adapter.add("text", handleAddLabel);

      function createSeries(field, fieldReadable, name, round) {
        // Set up series
        const series = chart2.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = field;
        series.dataFields.valueYReadable = fieldReadable;
        series.dataFields.dateX = "Date";
        series.name = name;
        series.tooltipHTML = `<table><tr><td>{name}: </td><td>{valueYReadable}</td></tr></table>`;
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color("#ffffff");
        series.tooltip.autoTextColor = false;
        series.tooltip.label.fill = am4core.color("#6E7E9D");
        series.tooltip.background.stroke = am4core.color("#D3E4EE");
        series.tooltip.background.strokeWidth = 1;
        series.strokeWidth = 2;
        series.tensionX = 0.95;
        series.tensionY = 0.95;
        series.stacked = true;
        series.fillOpacity = 0.2;
        const labelBullet = series.bullets.push(new am4charts.LabelBullet());
        labelBullet.locationY = 0.5;
        labelBullet.label.hideOversized = true;
        return series;
      }
      const series2 = createSeries("DownloadBandwidth", "DownloadBandwidthReadable", t("Download bandwidth"), false);
      const series3 = createSeries("UploadBandwidth", "TotalBandwidthReadable", t("Total bandwidth"), false);
      const scrollbar2 = new am4charts.XYChartScrollbar();
      chart2.scrollbarX = scrollbar2;
      chart2.scrollbarX.series.push(series2);
      chart2.scrollbarX.series.push(series3);
      chart2.scrollbarX.background.fill = am4core.color("#F5FBFF");
      chart2.scrollbarX.background.fillOpacity = 1;
      chart2.scrollbarX.unselectedOverlay.fill = am4core.color("#FFFFFF");
      chart2.scrollbarX.unselectedOverlay.fillOpacity = 0.7;
      chart2.scrollbarX.scrollbarChart.plotContainer.filters.clear();
      chart2.scrollbarX.parent = chart2.topAxesContainer;
      chart2.topAxesContainer.marginBottom = "36px";
      // Legend
      chart2.legend = new am4charts.Legend();
      customizeGrip(chart2.scrollbarX.startGrip);
      customizeGrip(chart2.scrollbarX.endGrip);
      const scrollAxis = chart2.scrollbarX.scrollbarChart.xAxes.getIndex(0);
      scrollAxis.renderer.grid.template.disabled = true;
      const scrollSeries2 = chart2.scrollbarX.scrollbarChart.series.getIndex(0);
      scrollSeries2.fillOpacity = 0;
      setChart2(chart2);
    }

    if (tabs === 0 && isFull(dataStorage)) {
      /* Chart code */
      // Themes begin
      am4core.useTheme(am4themes_animated);
      am4core.useTheme(am4themes_myTheme);
      // Themes end
      /* --------------------------------------------------------------------------------------------------------------------------*/

      // Create chart
      const chart = am4core.create("chartdiv2", am4charts.XYChart);
      chart.data = dataStorage;
      chart.plotContainer.background.fill = am4core.color("#ffffff");
      chart.plotContainer.background.fillOpacity = 1;
      chart.paddingTop = 0;
      chart.paddingBottom = "24px";
      chart.paddingLeft = "24px";
      chart.paddingRight = "24px";
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.baseInterval = {
        timeUnit: "day",
        count: 1,
      };
      dateAxis.tooltipDateFormat = "d MMMM";
      dateAxis.start = 0;
      dateAxis.keepSelection = true;
      dateAxis.groupData = false;
      dateAxis.renderer.labels.template.fill = am4core.color("#1B2A47");
      dateAxis.renderer.labels.template.location = 0.5;
      dateAxis.renderer.minLabelPosition = 0.03;
      dateAxis.renderer.maxLabelPosition = 0.98;
      dateAxis.cursorTooltipEnabled = false;
      setDateAxis1(dateAxis);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.tooltip.disabled = true;
      valueAxis.renderer.grid.template.location = 0.5;
      valueAxis.renderer.inside = true;
      valueAxis.renderer.labels.template.fill = am4core.color("#6E7E9D");
      valueAxis.renderer.labels.template.dy = -10;
      valueAxis.renderer.maxLabelPosition = 0.9;
      valueAxis.renderer.labels.template.adapter.add("text", handleAddLabel);
      valueAxis.cursorTooltipEnabled = false;
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = "Timestamp";
      series.dataFields.valueY = "UsedStorage";
      series.dataFields.valueYReadable = "UsedStorageReadable";
      series.tooltipHTML = `<table><tr><td>{valueYReadable}</td></tr></table>`;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color("#ffffff");
      series.tooltip.autoTextColor = false;
      series.tooltip.label.fill = am4core.color("#6E7E9D");
      series.tooltip.background.stroke = am4core.color("#D3E4EE");
      series.tooltip.background.strokeWidth = 1;
      series.fillOpacity = 0.3;
      series.tensionX = 0.95;
      series.tensionY = 0.95;
      series.color = am4core.color("#6E7E9D");
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.opacity = 0;
      chart.cursor.marginBottom = "20px";
      const scrollbar1 = new am4charts.XYChartScrollbar();
      chart.scrollbarX = scrollbar1;
      chart.scrollbarX.series.push(series);
      chart.scrollbarX.background.fill = am4core.color("#F5FBFF");
      chart.scrollbarX.background.fillOpacity = 1;
      chart.scrollbarX.scrollbarChart.plotContainer.filters.clear();
      chart.scrollbarX.unselectedOverlay.fill = am4core.color("#FFFFFF");
      chart.scrollbarX.unselectedOverlay.fillOpacity = 0.7;
      customizeGrip(chart.scrollbarX.startGrip);
      customizeGrip(chart.scrollbarX.endGrip);
      chart.scrollbarX.parent = chart.topAxesContainer;
      chart.topAxesContainer.marginBottom = "36px";
      const scrollSeries1 = chart.scrollbarX.scrollbarChart.series.getIndex(0);
      scrollSeries1.fillOpacity = 0;
      const scrollAxis1 = chart.scrollbarX.scrollbarChart.xAxes.getIndex(0);
      scrollAxis1.renderer.grid.template.disabled = true;
      chart.zoomOutButton.disabled = true;
      setChart1(chart);
      /* ---------------------------------------END-----------------------------------------------------------------------------------*/
    }
    // eslint-disable-next-line
  }, [tabs, dataStorage, dataBandwidth, language]);
  useEffect(() => {
    setTabsParentStage(tabsParent);
  }, [tabsParent]);

  useEffect(() => {
    const string1 = JSON.stringify(fromTo);
    const string2 = JSON.stringify(prevFromTo);
    // fix bug w/ loader
    if (string1 === string2) {
      indicatorRef.current?.();
    }
  }, [fromTo]);

  useEffect(() => {
    setTimeout(() => {
      if (tabsParentStage > -1) {
        _setTabs(tabsParentStage);
      }
    }, 500);
  }, [tabsParentStage]);

  return (
    <div className={style.block}>
      <div className={style.headerBlockTabsTitleRow}>
        <div className={style.headerBlockTabs}>{t("Analytics")}</div>
        <div className={style.tabs}>
          <div
            className={clsx(style.tab, tabs === 0 && style.tabActive)}
            onClick={() => {
              setTabs(0);
              setTabsParentStage(0);
            }}
          >
            {t("Storage")}
          </div>
          <div
            className={clsx(style.tab, tabs === 1 && style.tabActive)}
            onClick={() => {
              setTabs(1);
              setTabsParentStage(1);
            }}
          >
            {t("Bandwidth")}
          </div>
        </div>
      </div>

      <div style={{ display: tabs === 0 ? "block" : "none" }}>
        <div style={{ width: "100%" }} className={style.controls}>
          <div className={style.inputsRow}>
            <DayPickerRange
              classNameFrom="fromfield"
              classNameTo="tofield"
              handleFromChange={handleFromChange}
              handleToChange={handleToChange}
              from={fromTo?.fromInput || maxRange.from}
              to={fromTo?.toInput || maxRange.to}
              ref={ref1}
            />
          </div>
          <div className={style.buttons}>
            <button
              onClick={() => listenerButtons(1, "month", ChartIntervals.ONE_MONTH)}
              id="b1m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.ONE_MONTH && style.chartButtonActive
              )}
            >
              1&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(3, "month", ChartIntervals.THREE_MONTH)}
              id="b3m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.THREE_MONTH && style.chartButtonActive
              )}
            >
              3&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(6, "month", ChartIntervals.SIX_MONTH)}
              id="b6m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.SIX_MONTH && style.chartButtonActive
              )}
            >
              6&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(1, "year", ChartIntervals.YEAR)}
              id="b1y"
              className={clsx(style.chartButton, dateChartInterval === ChartIntervals.YEAR && style.chartButtonActive)}
            >
              1&nbsp;Y
            </button>
            <button
              onClick={() => listenerButtons(1, "max", ChartIntervals.MAX)}
              id="bmax"
              className={clsx(style.chartButton, dateChartInterval === ChartIntervals.MAX && style.chartButtonActive)}
            >
              MAX
            </button>
          </div>
        </div>

        {(!isFull(dataStorage) || dataStorageVisible?.length === 0) && (
          <EmptyList onClick={noop} textButton="" noBorder={true} noButton={true} icon={<SvgChart />}>
            {t("You don't have the data yet")}
          </EmptyList>
        )}

        {isFull(dataStorage) && dataStorageVisible?.length > 0 && (
          <div className={style.chartWrap}>
            <div id="chartdiv2" className={style.chartStorageContainer} />
          </div>
        )}
      </div>

      <div style={{ display: tabs === 1 ? "block" : "none" }}>
        <div className={style.controls}>
          <div className={style.inputsRow}>
            <DayPickerRange
              classNameFrom="fromfield2"
              classNameTo="tofield2"
              handleFromChange={handleFromChange}
              handleToChange={handleToChange}
              from={fromTo?.fromInput || maxRange.from}
              to={fromTo?.toInput || maxRange.to}
              ref={ref2}
            />
          </div>
          <div className={style.buttons}>
            <button
              onClick={() => listenerButtons(1, "month", ChartIntervals.ONE_MONTH)}
              id="b1m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.ONE_MONTH && style.chartButtonActive
              )}
            >
              1&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(3, "month", ChartIntervals.THREE_MONTH)}
              id="b3m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.THREE_MONTH && style.chartButtonActive
              )}
            >
              3&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(6, "month", ChartIntervals.SIX_MONTH)}
              id="b6m"
              className={clsx(
                style.chartButton,
                dateChartInterval === ChartIntervals.SIX_MONTH && style.chartButtonActive
              )}
            >
              6&nbsp;M
            </button>
            <button
              onClick={() => listenerButtons(1, "year", ChartIntervals.YEAR)}
              id="b1y"
              className={clsx(style.chartButton, dateChartInterval === ChartIntervals.YEAR && style.chartButtonActive)}
            >
              1&nbsp;Y
            </button>
            <button
              onClick={() => listenerButtons(1, "max", ChartIntervals.MAX)}
              id="bmax"
              className={clsx(style.chartButton, dateChartInterval === ChartIntervals.MAX && style.chartButtonActive)}
            >
              MAX
            </button>
          </div>
        </div>

        {(!isFull(dataBandwidth) || dataBandwidthVisible?.length === 0) && (
          <EmptyList onClick={noop} textButton="" noBorder={true} noButton={true} icon={<SvgChart />}>
            {t("You don't have the data yet")}
          </EmptyList>
        )}

        {isFull(dataBandwidth) && dataBandwidthVisible?.length > 0 && (
          <div className={clsx(style.chartWrap, style.chartWrap2)} style={{ background: "white" }}>
            <div id="chartdiv" className={style.chartStorageContainer} />
          </div>
        )}
      </div>
    </div>
  );
};
